import { useTranslation } from "react-i18next";
import { Button, Stack, Text, Radio, Group } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";
import { TextInput } from "@mantine/core";

import { useUpdateMySession } from "@hooks/mutations/onboarding/useUpdateMySession";

const schema = z.object({
  professional_category: z.string().min(1, "Please select your professional category"),
  professional_category_other: z.string().optional().or(z.literal("")),
}).refine(data => {
  if (data.professional_category === "Other Healthcare Professional" && (!data.professional_category_other || data.professional_category_other.trim() === "")) {
    return false;
  }
  return true;
}, {
  message: "Please specify your role",
  path: ["professional_category_other"]
});

type Inputs = z.infer<typeof schema>;

interface Step2Props {
  sessionData: any;
}

export const Step2ProfessionalCategory = ({ sessionData }: Step2Props) => {
  const { t } = useTranslation();
  const { mutate: updateSession, isLoading: isSaving } = useUpdateMySession();

  const form = useForm<Inputs>({
    validate: zodResolver(schema),
    initialValues: {
      professional_category: sessionData.professional_category || "",
      professional_category_other: sessionData.professional_category_other || "",
    },
  });

  const professionalCategory = form.values.professional_category;

  const handleSaveAndContinue = (values: Inputs) => {
    updateSession({
      professional_category: values.professional_category,
      professional_category_other: values.professional_category === "Other Healthcare Professional" ? values.professional_category_other : "",
      current_step: 3, // Advance to next step (Profile Details)
    });
  };

  return (
    <form onSubmit={form.onSubmit(handleSaveAndContinue)}>
      <Stack gap="lg">
        <Radio.Group
          name="professional_category"
          label={<Text fw={600} size="md">{t("onboarding-who-are-you")} <Text span c="red">*</Text></Text>}
          withAsterisk
          key={form.key("professional_category")}
          {...form.getInputProps("professional_category")}
        >
          <Stack mt="sm" gap="md">
            <Radio 
              value="Medical Doctor" 
              label={<Text fw={500}>{t("onboarding-medical-doctor")} <Text span c="dimmed" size="sm" fw={400} ml={4}>{t("onboarding-medical-doctor-desc")}</Text></Text>} 
            />
            <Radio 
              value="Allied Health Professional" 
              label={<Text fw={500}>{t("onboarding-allied")} <Text span c="dimmed" size="sm" fw={400} ml={4}>{t("onboarding-allied-desc")}</Text></Text>} 
            />
            <Radio 
              value="Pharmacist" 
              label={<Text fw={500}>{t("onboarding-pharmacist")} <Text span c="dimmed" size="sm" fw={400} ml={4}>{t("onboarding-pharmacist-desc")}</Text></Text>} 
            />
            <Radio 
              value="Licensed Health or Wellness Practitioner" 
              label={<Text fw={500}>{t("onboarding-wellness")} <Text span c="dimmed" size="sm" fw={400} ml={4}>{t("onboarding-wellness-desc")}</Text></Text>} 
            />
            <Radio 
              value="Other Healthcare Professional" 
              label={<Text fw={500}>{t("onboarding-other")}</Text>} 
            />
          </Stack>
        </Radio.Group>

        {professionalCategory === "Other Healthcare Professional" && (
          <TextInput
            label={t("onboarding-specify-role")}
            placeholder="e.g. Paramedic"
            withAsterisk
            key={form.key("professional_category_other")}
            {...form.getInputProps("professional_category_other")}
          />
        )}

        <Group justify="flex-end" mt="xl">
          <Button type="submit" loading={isSaving} size="md">
            {t("onboarding-save-continue")}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
